/**
 * Single source of truth for the live "unhedged" signal shared by the
 * Engine-position drawer and the main opener/unwinder/positions tables.
 *
 * The engine streams a per-rule `believedPosition` (see `entry.types.ts`)
 * carrying the signed net it believes we hold per platform. The NET we
 * surface on the tables is the signed `total YES − total NO` exposure:
 * positive = excess YES, negative = excess NO, 0 = delta-neutral.
 *
 * `believedPosition` already exposes the two signed nets with opposite
 * sign conventions:
 *   - `polyNet`  — `+` = net-long YES
 *   - `kalshiNet` — `+` = net-long NO
 * so the signed cross-platform net (excess-YES positive) is
 * `polyNet − kalshiNet`.
 */
import type { BelievedPosition } from '../types/entry.types';

/** A signed net above this many contracts is treated as unhedged (red). */
export const IMBALANCE_TOLERANCE = 0.5;

/**
 * Signed `total YES − total NO` exposure. Positive = excess YES.
 * `polyNet` is `+ = net-long YES`, `kalshiNet` is `+ = net-long NO`, so the
 * two combine as `polyNet − kalshiNet`.
 */
export const signedNet = (bp: BelievedPosition): number =>
  bp.polyNet - bp.kalshiNet;

/** `true` when the live believed position is unhedged beyond the tolerance. */
export const isUnhedged = (bp: BelievedPosition | null | undefined): boolean =>
  !!bp && Math.abs(signedNet(bp)) > IMBALANCE_TOLERANCE;

export interface NetBreakdown {
  /** Kalshi auto-netted net (`+` = net-long NO). */
  kalshiNet: number;
  /** Polymarket YES exposure = Path A (Poly-YES/Kalshi-NO) poly net. */
  polyYes: number;
  /** Polymarket NO exposure = Path B (Poly-NO/Kalshi-YES) poly net. */
  polyNo: number;
}

/**
 * Break the signed net into the three legs the tooltip shows. Path A is
 * Poly-YES/Kalshi-NO and Path B is Poly-NO/Kalshi-YES, so the per-path
 * Polymarket net is the YES (A) and NO (B) exposure respectively. A path
 * absent from `paths` (single-path / 0-fill) reads 0.
 */
export const netBreakdown = (bp: BelievedPosition): NetBreakdown => ({
  kalshiNet: bp.kalshiNet,
  polyYes: bp.paths.find((p) => p.path === 'a')?.polymarket.net ?? 0,
  polyNo: bp.paths.find((p) => p.path === 'b')?.polymarket.net ?? 0,
});

/**
 * Prefer the live engine's count when a believed position is present;
 * otherwise fall back to the slower REST `contracts` figure. Pass a
 * `platform` to read that leg's live net (`kalshiNet` / `polyNet`); omit it
 * to read the signed cross-platform net.
 */
export const selectLiveOrRestContracts = (
  bp: BelievedPosition | null | undefined,
  restContracts: number | undefined,
  platform?: 'kalshi' | 'polymarket',
): number | undefined => {
  if (bp) {
    if (platform === 'kalshi') return bp.kalshiNet;
    if (platform === 'polymarket') return bp.polyNet;
    return signedNet(bp);
  }
  return restContracts;
};
